import type { FieldTree } from '@angular/forms/signals';
import { validateStandardSchema } from '@angular/forms/signals';

import type {
    FormWidgetElement,
    LegacyPaymentFormDefault,
    LegacyPaymentFormZero,
    PaymentElementLegacy,
    PaymentFormWidgetEntity,
    PaymentFormWidgetRuleMessage,
} from '@sites/entity-core';
import {
    PAYMENT_FORM_WIDGET_RULE_MESSAGES,
    paymentFormLegacyDataRulesSchema,
    paymentFormLegacyDefaultV0Schema,
    paymentFormLegacyZeroV0Schema,
} from '@sites/entity-core';

import type { BaseLegacyEntityForm, BaseLegacyEntityFormOptions } from '../../core/create-base-legacy-entity-form';
import { configureCommonReadonly, createBaseLegacyEntityForm } from '../../core/create-base-legacy-entity-form';
import { arrayMove } from '../../core/array-move';
import { composeForm } from '../../core/compose-form';
import { extendedPositionPlugin, widgetPositionPlugin } from '../../plugins/widget/position.plugin';
import { visibilityPlugin } from '../../plugins/widget/visibility.plugin';
import { legacyWidgetEnvelopePlugin } from '../../plugins/widget/legacy-widget-envelope.plugin';

export type PaymentFormWidgetFormModel = PaymentFormWidgetEntity;
export type PaymentFormWidgetDataModel = PaymentFormWidgetEntity['data'];

/**
 * Payment-form accessors shared verbatim by both variant assemblers — the
 * CRM/address-book fields, the product configuration, the element and
 * payment-element arrays (with their immutable array sugar), styling groups and
 * view-mode flags. These are structurally identical on both variants (only
 * `position` differs, and that comes from the position plugins), so the lazy
 * `raw` thunk is narrowed to the default variant's FieldTree internally — the
 * form/button/social pattern.
 *
 * Exposed via getters so the FieldTree paths materialise lazily (after
 * `syncFromEntity` populates the model) — composed with {@link composeForm},
 * never object spread.
 *
 * **Beware optional branches**: `analyticsSettings` and the deprecated
 * `subscriptionMessage` may be absent from legacy persisted data — FieldTree
 * children only materialise for keys present in the model, so such writes go
 * **through the parent data node** (the missing-key trap) and reads should
 * prefer `value()`.
 */
function paymentFormSharedAccessors(raw: () => FieldTree<LegacyPaymentFormDefault> | FieldTree<LegacyPaymentFormZero>) {
    const r = raw as () => FieldTree<LegacyPaymentFormDefault>;
    return {
        // --- CRM integration ---
        get selectedAddressBook() {
            return r().data.selectedAddressBook;
        },
        setSelectedAddressBook: (value: PaymentFormWidgetDataModel['selectedAddressBook']) => {
            r().data.selectedAddressBook().value.set(value);
        },
        get selectedPipeline() {
            return r().data.selectedPipeline;
        },
        setSelectedPipeline: (value: PaymentFormWidgetDataModel['selectedPipeline']) => {
            r().data.selectedPipeline().value.set(value);
        },
        get selectedPipelineStep() {
            return r().data.selectedPipelineStep;
        },
        setSelectedPipelineStep: (value: PaymentFormWidgetDataModel['selectedPipelineStep']) => {
            r().data.selectedPipelineStep().value.set(value);
        },
        get selectedPipelineSettings() {
            return r().data.selectedPipelineSettings;
        },
        setSelectedPipelineSettings: (value: PaymentFormWidgetDataModel['selectedPipelineSettings']) => {
            r().data.selectedPipelineSettings().value.set(value);
        },
        get dealName() {
            return r().data.dealName;
        },
        setDealName: (value: PaymentFormWidgetDataModel['dealName']) => {
            r().data.dealName().value.set(value);
        },

        // --- Address book / tags ---
        get createAddressBook() {
            return r().data.createAddressBook;
        },
        setCreateAddressBook: (value: boolean) => {
            r().data.createAddressBook().value.set(value);
        },
        get addCrmTags() {
            return r().data.addCrmTags;
        },
        setAddCrmTags: (value: boolean) => {
            r().data.addCrmTags().value.set(value);
        },
        get contactTags() {
            return r().data.contactTags;
        },
        setContactTags: (value: number[]) => {
            r().data.contactTags().value.set(value);
        },
        get addAddressBookTags() {
            return r().data.addAddressBookTags;
        },
        setAddAddressBookTags: (value: boolean) => {
            r().data.addAddressBookTags().value.set(value);
        },
        get contactAddressBookTags() {
            return r().data.contactAddressBookTags;
        },
        setContactAddressBookTags: (value: number[]) => {
            r().data.contactAddressBookTags().value.set(value);
        },

        // --- Product ---
        get paymentFormName() {
            return r().data.paymentFormName;
        },
        setPaymentFormName: (value: PaymentFormWidgetDataModel['paymentFormName']) => {
            r().data.paymentFormName().value.set(value);
        },
        get productType() {
            return r().data.productType;
        },
        setProductType: (value: PaymentFormWidgetDataModel['productType']) => {
            r().data.productType().value.set(value);
        },
        get productName() {
            return r().data.productName;
        },
        setProductName: (value: PaymentFormWidgetDataModel['productName']) => {
            r().data.productName().value.set(value);
        },
        get productSettings() {
            return r().data.productSettings;
        },
        setProductSettings: (value: PaymentFormWidgetDataModel['productSettings']) => {
            r().data.productSettings().value.set(value);
        },

        // --- Payment elements (array) ---
        get paymentElements() {
            return r().data.paymentElements;
        },
        setPaymentElements: (value: PaymentElementLegacy[]) => {
            r().data.paymentElements().value.set(value);
        },
        paymentElementAt: (index: number): FieldTree<PaymentElementLegacy> | undefined => {
            return (r().data.paymentElements as unknown as Record<number, FieldTree<PaymentElementLegacy>>)[index];
        },
        addPaymentElement: (item: PaymentElementLegacy) => {
            r()
                .data.paymentElements()
                .value.update((arr) => [...arr, item]);
        },
        removePaymentElement: (index: number) => {
            r()
                .data.paymentElements()
                .value.update((arr) => arr.filter((_, i) => i !== index));
        },
        movePaymentElement: (from: number, to: number) => {
            r()
                .data.paymentElements()
                .value.update((arr) => arrayMove(arr, from, to));
        },
        updatePaymentElement: (index: number, patch: Partial<PaymentElementLegacy>) => {
            r()
                .data.paymentElements()
                .value.update((arr) => arr.map((e, i) => (i === index ? { ...e, ...patch } : e)));
        },

        // --- Form content (array) ---
        get elements() {
            return r().data.elements;
        },
        setElements: (value: FormWidgetElement[]) => {
            r().data.elements().value.set(value);
        },
        elementAt: (index: number): FieldTree<FormWidgetElement> | undefined => {
            return (r().data.elements as unknown as Record<number, FieldTree<FormWidgetElement>>)[index];
        },
        addElement: (item: FormWidgetElement) => {
            r()
                .data.elements()
                .value.update((arr) => [...arr, item]);
        },
        removeElement: (index: number) => {
            r()
                .data.elements()
                .value.update((arr) => arr.filter((_, i) => i !== index));
        },
        moveElement: (from: number, to: number) => {
            r()
                .data.elements()
                .value.update((arr) => arrayMove(arr, from, to));
        },
        updateElement: (index: number, patch: Partial<FormWidgetElement>) => {
            r()
                .data.elements()
                .value.update((arr) => arr.map((e, i) => (i === index ? { ...e, ...patch } : e)));
        },

        // --- Styling groups (FieldTree branch + whole-value setter) ---
        get inputSettings() {
            return r().data.inputSettings;
        },
        setInputSettings: (value: PaymentFormWidgetDataModel['inputSettings']) => {
            r().data.inputSettings().value.set(value);
        },
        get formButtonSettings() {
            return r().data.formButtonSettings;
        },
        setFormButtonSettings: (value: PaymentFormWidgetDataModel['formButtonSettings']) => {
            r().data.formButtonSettings().value.set(value);
        },
        get additionalFormSettings() {
            return r().data.additionalFormSettings;
        },
        setAdditionalFormSettings: (value: PaymentFormWidgetDataModel['additionalFormSettings']) => {
            r().data.additionalFormSettings().value.set(value);
        },
        /** Optional branch — may be absent on legacy data; prefer `value()` reads. */
        get analyticsSettings() {
            return r().data.analyticsSettings;
        },
        setAnalyticsSettings: (value: PaymentFormWidgetDataModel['analyticsSettings']) => {
            // Whole-data write: the branch is optional, so its FieldTree child
            // may not exist yet (the missing-key trap) — writing through the
            // parent both sets the value and materialises the child.
            r()
                .data()
                .value.update((data) => ({ ...data, analyticsSettings: value }));
        },

        // --- View mode ---
        get formView() {
            return r().data.formView;
        },
        setFormView: (value: PaymentFormWidgetDataModel['formView']) => {
            r().data.formView().value.set(value);
        },
        get isShowModal() {
            return r().data.isShowModal;
        },
        setIsShowModal: (value: boolean) => {
            r().data.isShowModal().value.set(value);
        },
        get modalTextHint() {
            return r().data.modalTextHint;
        },
        setModalTextHint: (value: PaymentFormWidgetDataModel['modalTextHint']) => {
            r().data.modalTextHint().value.set(value);
        },
        get isSynchronizeStyle() {
            return r().data.isSynchronizeStyle;
        },
        setIsSynchronizeStyle: (value: boolean) => {
            r().data.isSynchronizeStyle().value.set(value);
        },

        // --- Publish checks ---
        /**
         * Every failed publish check of the payment form, one translation key per issue. The same
         * `paymentFormLegacyDataRulesSchema` errors also sit on the fields they name
         * (`form.dealName().errors()`); `payment_form_invalid` sits on `data` itself.
         */
        get errors(): PaymentFormWidgetRuleMessage[] {
            return r()
                .data()
                .errorSummary()
                .map((error) => error.message)
                .filter((message): message is PaymentFormWidgetRuleMessage =>
                    PAYMENT_FORM_WIDGET_RULE_MESSAGES.includes(message as PaymentFormWidgetRuleMessage)
                );
        },
    };
}

/**
 * Per-variant accessor assemblers. Exactly like form/button/social, the two
 * payment-form variants share every accessor and differ **only** in the
 * `position` branch type: the default form's grid is the simple per-device
 * `WidgetPosition` (via {@link widgetPositionPlugin}), the zero form's is the
 * full `ExtendedPosition` (anchors, heights, changed-flags — via
 * {@link extendedPositionPlugin}).
 */
function assembleDefaultPaymentFormWidget(base: BaseLegacyEntityForm<LegacyPaymentFormDefault>) {
    const _raw = base._raw;

    // Checked widening (no cast): the variant form is assignable to the
    // entity-union form, so the dispatcher's union return collapses into a
    // single useful shape for union-only callers.
    const widenedBase: BaseLegacyEntityForm<PaymentFormWidgetEntity> = base;

    return composeForm(
        widenedBase,
        legacyWidgetEnvelopePlugin(() => _raw),
        paymentFormSharedAccessors(() => _raw),
        visibilityPlugin(() => _raw.data),
        // Default-only layout — the simple per-device position grid.
        widgetPositionPlugin(() => _raw.data)
    );
}

function assembleZeroPaymentFormWidget(base: BaseLegacyEntityForm<LegacyPaymentFormZero>) {
    const _raw = base._raw;

    const widenedBase: BaseLegacyEntityForm<PaymentFormWidgetEntity> = base;

    return composeForm(
        widenedBase,
        legacyWidgetEnvelopePlugin(() => _raw),
        paymentFormSharedAccessors(() => _raw),
        visibilityPlugin(() => _raw.data),
        // Zero-only layout — the full extended-position grid.
        extendedPositionPlugin(() => _raw.data)
    );
}

/**
 * Signal-form view of a **default-placement** payment-form widget. Validates
 * against the legacy default envelope schema, which permits an absent `type`
 * field for parity with pre-discriminator persisted data on disk.
 */
export function createLegacyPaymentFormDefaultForm(options?: BaseLegacyEntityFormOptions<LegacyPaymentFormDefault>) {
    const base = createBaseLegacyEntityForm<LegacyPaymentFormDefault>((schemaPath) => {
        validateStandardSchema(schemaPath, paymentFormLegacyDefaultV0Schema);
        validateStandardSchema(schemaPath.data, paymentFormLegacyDataRulesSchema);
        configureCommonReadonly(schemaPath);
    }, options);

    return assembleDefaultPaymentFormWidget(base);
}

/**
 * Signal-form view of a **zero-block-placement** payment-form widget. Validates
 * against the legacy zero envelope schema, which requires `type: 'zero'` on the
 * envelope (the host-app factory copies `meta.type` onto the entity at creation
 * time). Diverges from {@link createLegacyPaymentFormDefaultForm} only on
 * `position`.
 */
export function createLegacyPaymentFormZeroForm(options?: BaseLegacyEntityFormOptions<LegacyPaymentFormZero>) {
    const base = createBaseLegacyEntityForm<LegacyPaymentFormZero>((schemaPath) => {
        validateStandardSchema(schemaPath, paymentFormLegacyZeroV0Schema);
        validateStandardSchema(schemaPath.data, paymentFormLegacyDataRulesSchema);
        configureCommonReadonly(schemaPath);
    }, options);

    return assembleZeroPaymentFormWidget(base);
}

/**
 * Variant dispatcher. Callers that only know `PaymentFormWidgetEntity` (the
 * union of default + zero) use this — `WebLegacyWidgetSignalFormService` for instance
 * — and the dispatcher reads the variant tag from `options.initialData?.type`
 * to pick the matching per-variant factory.
 */
export function createPaymentFormWidgetForm(options?: BaseLegacyEntityFormOptions<PaymentFormWidgetEntity>) {
    const variant = options?.initialData?.type;

    if (variant === 'zero') {
        return createLegacyPaymentFormZeroForm(options as BaseLegacyEntityFormOptions<LegacyPaymentFormZero>);
    }

    return createLegacyPaymentFormDefaultForm(options as BaseLegacyEntityFormOptions<LegacyPaymentFormDefault>);
}

export type PaymentFormWidgetForm = ReturnType<typeof createPaymentFormWidgetForm>;
export type PaymentFormWidgetDefaultForm = ReturnType<typeof createLegacyPaymentFormDefaultForm>;
export type PaymentFormWidgetZeroForm = ReturnType<typeof createLegacyPaymentFormZeroForm>;
